/**
 * Quotas Module - TanStack Query Hooks
 *
 * This module provides all hooks for self-service quota purchase operations including:
 * - Quota packages listing
 * - Order creation, listing, detail, cancellation
 * - Transaction history with filters
 *
 * All hooks use centralized query keys from queryKeys.ts
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  cancelQuotaOrder,
  createQuotaOrder,
  exportQuotasCSV,
  getQuotaOrder,
  getQuotaOrders,
  getQuotaPackages,
  getQuotaTransactions,
} from "api/quotas";

import { PaginationTable } from "js/components/types";

import { OrderFilters, TransactionFilters } from "js/pages/Quotas/types";
import type { CreateOrderPayload } from "js/pages/Quotas/types";

import { toast } from "../utils/toast";
import { queryKeys } from "./queryKeys";

// ============================================================================
// QUERY HOOKS (GET)
// ============================================================================

/**
 * Get all available quota packages
 * @returns Quota packages list (7 PF + 7 PM) with loading and error states
 */
export const useGetQuotaPackages = () => {
  return useQuery({
    queryKey: queryKeys.quotas.packages(),
    queryFn: getQuotaPackages,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: "always",
  });
};

/**
 * Get paginated list of quota orders
 * @param pagination - Pagination parameters (current page, page size)
 * @returns Orders list with loading and error states
 */
export const useGetQuotaOrders = (
  pagination: PaginationTable,
  filters?: OrderFilters,
) => {
  return useQuery({
    queryKey: queryKeys.quotas.orders.list({ pagination, filters }),
    queryFn: () => getQuotaOrders(pagination, filters),
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });
};

/**
 * Get a single quota order by ID
 * Supports polling via refetchInterval for PAYMENT_IN_PROGRESS orders
 * @param orderId - The order ID to fetch
 * @param enabled - Whether the query should execute
 * @param refetchInterval - Polling interval in ms, or false to disable
 * @returns Order detail with loading and error states
 */
export const useGetQuotaOrder = (
  orderId: string,
  enabled: boolean = true,
  refetchInterval: number | false = false,
) => {
  return useQuery({
    queryKey: queryKeys.quotas.orders.detail(orderId),
    queryFn: () => getQuotaOrder(orderId),
    enabled: !!orderId && enabled,
    refetchInterval,
  });
};

/**
 * Get paginated quota transactions with optional filters
 * @param pagination - Pagination parameters
 * @param filters - Optional filters for quote_type and transaction_type
 * @returns Transactions list with loading and error states
 */
export const useGetQuotaTransactions = (
  pagination: PaginationTable,
  filters?: TransactionFilters,
) => {
  return useQuery({
    queryKey: queryKeys.quotas.transactions.list({ pagination, filters }),
    queryFn: () => getQuotaTransactions(pagination, filters),
    staleTime: 30 * 1000,
    refetchOnMount: "always",
  });
};

// ============================================================================
// MUTATION HOOKS (CREATE, CANCEL)
// ============================================================================

/**
 * Create a new quota order
 * Invalidates: quota orders list
 */
export const useCreateQuotaOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateOrderPayload) => createQuotaOrder(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.quotas.orders.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.credits(),
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error || "Error al crear la orden de compra";
      toast.error(message);
    },
  });
};

/**
 * Cancel a pending or in-progress quota order
 * Invalidates: order detail, orders list, organization credits
 */
export const useCancelQuotaOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (orderId: string) => cancelQuotaOrder(orderId),
    onSuccess: (_data, orderId) => {
      toast.success("Orden cancelada exitosamente");
      queryClient.invalidateQueries({
        queryKey: queryKeys.quotas.orders.detail(orderId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.quotas.orders.all(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.organization.credits(),
      });
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.error || "Error al cancelar la orden";
      toast.error(message);
    },
  });
};

/**
 * Export quotas data as CSV file download
 * Triggers a browser file download with the CSV blob
 */
export const useExportQuotasCSV = () => {
  return useMutation({
    mutationFn: ({
      type,
      filters,
    }: {
      type: "transactions" | "orders" | "all";
      filters?: {
        quote_type?: string;
        transaction_type?: string;
        date_from?: string;
        date_to?: string;
        performed_by?: string;
      };
    }) => exportQuotasCSV(type, filters),
    onSuccess: (data, variables) => {
      const blob = data instanceof Blob ? data : new Blob([data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const filenames = { orders: "cuotas_ordenes.csv", transactions: "cuotas_transacciones.csv", all: "cuotas_actividad.csv" };
      link.download = filenames[variables.type];
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Archivo exportado exitosamente");
    },
    onError: () => {
      toast.error("Error al exportar los datos");
    },
  });
};
